import { Component, OnInit, DoCheck } from '@angular/core';
import { WorkflowPageNavigationService } from '../../../../service/workflow-page-navigation/workflow-page-navigation.service';
import { CorePageBaseComponent } from '../core-page-base.component';
import { WorkflowWidgetEventServiceService } from '../../../../widget/workflow-widget-event.service';
import { WorkflowService } from '../../../../service/workflow/workflow.service';
import { AlloyService } from '@workflow/ecp-workflow/dynamic-app/service/alloy/alloy.service';
import { CardInstance } from '@workflow/class/card.service';

@Component({
  selector: 'cu-dynamic-page-workflow-progress',
  templateUrl: './page-workflow-progress.component.html',
  standalone: false
})
export class PageWorkflowProgressComponent extends CorePageBaseComponent implements OnInit, DoCheck {
  constructor(
    public workflowPageNavigationService: WorkflowPageNavigationService,
    public widgetEventService: WorkflowWidgetEventServiceService,
    private readonly workflowService: WorkflowService,
    private readonly alloyService: AlloyService
  ) {
    super(workflowPageNavigationService, widgetEventService);
  }

  /**
   * visible cards collection
   */
  public get visibleCards(): CardInstance[] {
    return this.page.helper.pageVisibleCards;
  }

  public get pageTitle(): string {
    return this.page.customTitleOrDefault;
  }
  /** property holds list of page ids irrespective of visible property */
  private pageList: string[] = [];
  private totalPageCount = 1;
  public flowType: 'OLA' | 'OAO';
  ngOnInit(): void {
    this.flowType = this.flow.id.includes('ola') ? 'OLA' : 'OAO';
    this.getpageIds();

    this.workflowPageNavigationService.updateNavState(this.page, true);
    this.workflowPageNavigationService.reset();
    this.alloyService.trackIfEnabled(this.page.id);
  }

  public ngDoCheck(): void {
    this.page.helper.whenValidationChange(this.refreshNavigation);
    this.page.whenPageTitleChanged(this.refreshNavigation);
  }

  /** US#252606  the progress for loan selection and eligiblity page will be excluded.
   * both page will show default 5%
   */
  isExcludedPage(pageId: string): boolean {
    const pageidQuery = ['oao-membership', 'ola-consumer-loan-type', 'action-poll-appid'];
    return pageidQuery.some((a) => pageId.toLowerCase().includes(a));
  }
  /** method to refresh the list of page id and total number of pages
   * it will be called once on page load
   */
  private getpageIds(): void {
    const configPages = this.flow.config.pages.filter(
      (a) => !this.isExcludedPage(a) && !this.workflowService.isHiddenInAdmin(a, this.flow.id)
    );
    const totalConfigPage = configPages.length;
    // Total pages visible in flow not including review
    this.totalPageCount = this.flow.allPages.filter((page) => {
      return !page.isHiddenInAdmin && !this.isExcludedPage(page.id);
    }).length;

    // taking maximum posiible page count
    if (this.totalPageCount < totalConfigPage) {
      this.totalPageCount = totalConfigPage;
      this.pageList = configPages.map((id) => id.replace('#', ''));
    } else {
      this.pageList = this.flow.allPages.filter((page) => !page.isHiddenInAdmin && !this.isExcludedPage(page.id)).map((a) => a.id);
    }
  }

  /**
   * Calculates the progress percentage based on the completed pages over total pages.
   *
   * If the flow is 'OAO' or 'OLA', it returns a fixed percentage.
   * Otherwise, it filters out co-borrower and review pages, then calculates the progress
   * based on the completed pages over the total filtered pages.
   *
   * @returns The progress percentage. If no flow pages are found, returns 0.
   */
  public progress(): number {
    if (this.flow?.allPages) {
      // If on initial OAO or OLA flow, return progress as less than 3%.
      // Reason: Initial flow only contains 2-4 pages and does not represent
      // the actual number of pages the user may actually need to complete.
      if (this.flow.config.id === 'oao' || this.flow.config.id === 'ola' || this.page.id.includes('-eligibility')) {
        return 3;
      }
      /// taking index of first incompleted page so that we will get count of all completed page(considering invisible page as completed)
      /**
       * considering page incompleted if satisfies below condision
       * 1. !page.helper.isPageComplete - has any validation error
       * 2. workflowPageNavigationService.isCompleted - user not touched and not completed the page
       * 3. visible - not hidden in admin portal / any condition
       */
      let firstIncompletePageId = '';
      let completedPagesCount = 0;
      // getting first incompleted and untouched page to calculate actual user progress
      this.flow.pages.forEach((page) => {
        if (
          !firstIncompletePageId &&
          !this.isExcludedPage(page.id) &&
          (!this.workflowPageNavigationService.isCompleted(page.id) || !page.helper.isPageComplete)
        ) {
          firstIncompletePageId = page.id;
        } else if (this.workflowPageNavigationService.isCompleted(page.id)) firstIncompletePageId = undefined;
      });

      // if no incomplete page found then consider all page completed
      if (firstIncompletePageId) {
        this.pageList.forEach((a, i) => {
          if (firstIncompletePageId.includes(a)) completedPagesCount = i;
        });
      } else completedPagesCount = this.pageList.length;
      // We do not want the progress indicator to ever be 100% - even on the last page
      // Reason: This will give the user the visual queue that they are not done
      // until they click submit on the review page
      return (completedPagesCount / this.totalPageCount) * 98.5;
    }
    return 0;
  }

  /** Call back on page validation change */
  private readonly refreshNavigation = () => {
    if (this.page) {
      /** Passing isPageVisited as true.
       * If not on race condition page state got overriden with initial state when user moving fast
       */
      this.workflowPageNavigationService.updateNavState(this.page, true);
    }
  };
}
